import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import TopBar from "@/components/TopBar";
import PostParcel from "@/components/PostParcel";
import PostTrip from "@/components/PostTrip";
import MyParcels from "@/components/MyParcels";
import { useNavigate, useSearchParams } from "react-router-dom";
import LocationReporter from "@/components/LocationReporter";
import DeliveryList from "@/components/DeliveryList";
import CarrierEarnings from "@/components/CarrierEarnings";
import MyTrips from "@/components/MyTrips";
import AvailableParcels from "@/components/AvailableParcels";
import CodNudgeBanner from "@/components/CodNudgeBanner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { ShieldAlert } from "lucide-react";
import CarrierSetup from "@/components/CarrierSetup";
import { RoleChooser, NamePrompt } from "@/components/RoleChooser";
import LegalGate from "@/components/LegalGate";

const Home = () => {
  const { user, mode, modeChosen, refresh, switchMode } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tripFilter, setTripFilter] = useState(null);
  const [wallet, setWallet] = useState(0);
  const [senderTab, setSenderTab] = useState("new");
  const [carrierTab, setCarrierTab] = useState("trip");

  const loadWallet = async () => {
    try { const w = await api.get("/wallet"); setWallet(w.data.balance || 0); } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadWallet(); }, []);
  useEffect(() => {
    if (params.get("carry") === "available") {
      if (mode !== "carrier") switchMode("carrier");
      setCarrierTab("available");
      setTripFilter(params.get("trip"));
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  const needsPayoutDetails = mode === "carrier" && user?.has_accepted_parcel &&
    !(user?.account_number && user?.ifsc_code && user?.account_holder_name) && !user?.upi_id;

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <TopBar balance={wallet}/>
      <LegalGate />
      <main className="w-full max-w-6xl mx-auto px-4 py-6 space-y-5 overflow-x-hidden">
        <NamePrompt/>
        {modeChosen && mode === "carrier" && <CarrierSetup/>}
        <LocationReporter/>
        {needsPayoutDetails && (
          <Alert className="rounded-2xl border-amber-200 bg-amber-50" data-testid="payout-details-banner">
            <ShieldAlert className="w-4 h-4 text-amber-700"/>
            <AlertTitle className="text-amber-900">Add your bank details to withdraw your earnings.</AlertTitle>
            <AlertDescription className="text-amber-800/80 text-sm mt-1">
              Your payout is held until a bank account or UPI ID is added and verified.
              <button onClick={() => setCarrierTab("earnings")} className="ml-2 underline font-medium text-amber-900" data-testid="payout-banner-add-btn">Add details</button>
            </AlertDescription>
          </Alert>
        )}

        {!modeChosen ? <RoleChooser/> : mode === "sender" ? (
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold">Hi {user?.first_name || "there"} · Send a parcel</h1>
            <p className="text-slate-500 mt-1 text-sm">One transparent price. The carrier is paid only after you confirm delivery with a PIN.</p>
            <Tabs value={senderTab} onValueChange={setSenderTab} className="mt-5">
              <div className="overflow-x-auto -mx-4 px-4 pb-1"><TabsList className="rounded-full bg-slate-100 w-max">
                <TabsTrigger value="new" data-testid="tab-new-parcel" className="rounded-full">New parcel</TabsTrigger>
                <TabsTrigger value="browse" data-testid="tab-browse" className="rounded-full">Browse travellers</TabsTrigger>
                <TabsTrigger value="mine" data-testid="tab-my-shipments" className="rounded-full">My shipments</TabsTrigger>
              </TabsList></div>
              <TabsContent value="new" className="mt-5">
                <PostParcel onCreated={(p) => nav(`/parcel/${p.id}/browse`)}/>
              </TabsContent>
              <TabsContent value="browse" className="mt-5">
                <div className="text-sm text-slate-500 mb-3">Pick a parcel to browse travellers going that way.</div>
                <MyParcels openOnly emptyText="No parcels waiting for a traveller. Create one in New parcel."/>
              </TabsContent>
              <TabsContent value="mine" className="mt-5">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">My parcels</div>
                <MyParcels/>
                <div className="text-xs uppercase tracking-wider text-slate-500 mt-6 mb-2">Deliveries</div>
                <DeliveryList role="sender"/>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold">Earn on your route</h1>
            <p className="text-slate-500 mt-1 text-sm">Full transparency on your earnings and the platform fee.</p>
            <Tabs value={carrierTab} onValueChange={setCarrierTab} className="mt-5">
              <CodNudgeBanner onGoFindParcels={() => setCarrierTab("available")}/>
              <div className="overflow-x-auto -mx-4 px-4 pb-1"><TabsList className="rounded-full bg-slate-100 w-max">
                <TabsTrigger value="trip" data-testid="tab-post-trip" className="rounded-full">Post trip</TabsTrigger>
                <TabsTrigger value="mine" data-testid="tab-my-trips" className="rounded-full">My trips</TabsTrigger>
                <TabsTrigger value="available" data-testid="tab-available-parcels" className="rounded-full">Find parcels</TabsTrigger>
                <TabsTrigger value="incoming" data-testid="tab-incoming" className="rounded-full">Requests</TabsTrigger>
                <TabsTrigger value="earnings" data-testid="tab-earnings" className="rounded-full">Earnings</TabsTrigger>
              </TabsList></div>
              <TabsContent value="trip" className="mt-5"><PostTrip onCreated={(t) => { setTripFilter(t?.id || null); setCarrierTab("available"); }}/></TabsContent>
              <TabsContent value="mine" className="mt-5"><MyTrips/></TabsContent>
              <TabsContent value="available" className="mt-5"><AvailableParcels tripId={tripFilter} onClearTrip={() => setTripFilter(null)}/></TabsContent>
              <TabsContent value="incoming" className="mt-5"><DeliveryList role="carrier"/></TabsContent>
              <TabsContent value="earnings" className="mt-5"><CarrierEarnings user={user} onRefresh={() => { loadWallet(); refresh(); }}/></TabsContent>
            </Tabs>
          </div>
        )}
      </main>
    </div>
  );
};

export default Home;
